import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { backtestResultState } from '#state/trades'; // Ваш импорт
import {
  candlesSelector,
  tempCandlesSelector,
} from '#state/data';
import { getCandleChart } from '#state/gui';


const marketFee = 0.0005
const limitFee = 0.0002
const cashbackRate = 0.35

// Утилиты (без изменений)

function formatNumber(num, significantDigits = 3) {
  if (typeof num !== 'number') return num
  // Включаем массив суффиксов внутрь функции для полной инкапсуляции
  const suffixes = {
    1000: 'K',
    1000000: 'M',
    1000000000: 'B',
    1000000000000: 'T'
  };

  const absNum = Math.abs(num);
  const sigDelta = Math.max(0, significantDigits - 3)

  if (absNum >= 1000) {
    let scale = 1;
    let suffix = '';
    let scaledNum = num;

    // Определяем нужный делитель и суффикс
    while (scaledNum >= 1000 && scale <= 1e12) {
      scale *= 1000;
      scaledNum = num / scale;
      suffix = suffixes[scale] || '';
    }

    // Округляем число в зависимости от величины
    if (scaledNum >= 100) {
      return (scaledNum.toFixed(sigDelta) + suffix);
    } else if (scaledNum >= 10) {
      return (scaledNum.toFixed(sigDelta + 1) + suffix);
    } else {
      return (scaledNum.toFixed(sigDelta + 2) + suffix);
    }
  } else if (absNum < 1) {
    // Используем toPrecision для чисел меньше 1
    return num.toPrecision(significantDigits);
  } else {

    // Числа от 1 до 999, применяем логику похожую на большие числа, но без суффикса
    if (num >= 100) {
      return num.toFixed(sigDelta);
    } else if (num >= 10) {
      return num.toFixed(sigDelta + 1);
    } else {
      return num.toFixed(sigDelta + 2);
    }
  }
}

const formatTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp * 1000).toLocaleString();
};


// ========================================================================
// 1. ИСПРАВЛЕННАЯ ФУНКЦИЯ: calculateSingleTradePnl
//    Корректно обрабатывает частично и полностью открытые/закрытые сделки.
// ========================================================================
// ЗАМЕНИТЕ ВАШУ ТЕКУЩУЮ ФУНКЦИЮ НА ЭТУ:
const calculateSingleTradePnl = (entries, exits, direction, lastCandle, marketFee, cashbackRate) => {
  if (!entries?.length) {
    return 0;
  }

  const allActions = [...entries, ...(exits || [])];

  // 1. Рассчитываем чистый денежный поток (сумма всех deltaNet).
  const netCashFlow = allActions.reduce((sum, action) => sum + (action.trade?.deltaNet || 0), 0);

  const lastAction = allActions[allActions.length - 1];
  // 2. Получаем стоимость оставшейся части позиции.
  const remainingPositionSize = lastAction.trade?.positionSize;

  let unrealizedPnl = 0;

  // 3. Рассчитываем "бумажную" прибыль/убыток для этой оставшейся части.
  if (remainingPositionSize > 1e-6) {
    const lastEntry = entries[entries.length - 1];
    const avgEntryPrice = lastEntry.trade?.avgEntryPrice;
    const currentPrice = lastCandle?.close;

    if (avgEntryPrice && currentPrice) {
      let deltaPercent = 0;
      if (direction === 'long') {
        deltaPercent = (currentPrice - avgEntryPrice) / avgEntryPrice;
      } else {
        deltaPercent = (avgEntryPrice - currentPrice) / avgEntryPrice;
      }
      const grossUnrealized = remainingPositionSize * deltaPercent;
      // Учитываем комиссию за будущее закрытие остатка
      const closingFee = remainingPositionSize * marketFee * (1 - cashbackRate);
      unrealizedPnl = grossUnrealized - closingFee;
    }
  }

  // 4. ФИНАЛЬНАЯ ПРАВИЛЬНАЯ ФОРМУЛА:
  // Чтобы получить итоговый PnL, к чистому денежному потоку нужно "вернуть"
  // стоимость актива, который все еще у нас, и добавить его нереализованный PnL.
  return netCashFlow + (remainingPositionSize || 0) + unrealizedPnl;
};


// ========================================================================
// 2. ИСПРАВЛЕННАЯ ФУНКЦИЯ: calculateMetrics
//    Считает статистику (PF, WinRate) только по полностью закрытым сделкам.
// ========================================================================
const calculateMetrics = (actions, direction, lastCandle) => {
  let grossProfit = 0;
  let grossLoss = 0;
  let winCount = 0;
  let tradeCount = 0;
  let totalNetProfit = 0;
  let closedTradeCount = 0;

  if (!actions || actions.length === 0) {
    return { totalNetProfit: 0, profitFactor: 0, winRate: 0, totalTrades: 0 };
  }

  for (let i = 0; i < actions.length; i++) {
    const trade = actions[i]
    const entries = trade.entries || [];
    const exits = trade.exits || [];

    tradeCount++;

    // --- Исправление: корректный расчет PnL для PortfolioManager ---
    // Если trade из PortfolioManager (есть поле pnl), используем его напрямую (и инвертируем для short)
    let netPnl;
    if (typeof trade.pnl === 'number') {
      netPnl = trade.direction === 'short' ? -trade.pnl : trade.pnl;
    } else {
      netPnl = calculateSingleTradePnl(entries, exits, direction, lastCandle, marketFee, cashbackRate);
    }
    totalNetProfit += netPnl;

    // Определяем, полностью ли закрыта сделка, для сбора статистики.
    const allActions = [...entries, ...(exits || [])];
    const lastAction = allActions[allActions.length - 1];
    const isFullyClosed = (lastAction.trade?.positionSize || 0) < 1e-6;

    if (isFullyClosed) {
      closedTradeCount++;
      // Для полностью закрытой сделки `netPnl` содержит итоговый реализованный результат.
      if (netPnl > 0) {
        grossProfit += netPnl;
        winCount++;
      } else if (netPnl < 0) {
        grossLoss += Math.abs(netPnl);
      }
      // Если netPnl === 0, не учитываем ни в grossProfit, ни в grossLoss
    }
  }

  // --- Исправление: profitFactor считается только по закрытым сделкам PortfolioManager ---
  // Если все сделки из PortfolioManager, считаем profitFactor по их pnl (инвертируя для short)
  const allPortfolioManager = actions.every(
    t => typeof t.pnl === 'number'
  );
  if (allPortfolioManager) {
    let pfGrossProfit = 0;
    let pfGrossLoss = 0;
    let pfWinCount = 0;
    let pfClosedCount = 0;
    for (let i = 0; i < actions.length; i++) {
      const t = actions[i];
      if (typeof t.pnl === 'number' && t.status === 'closed') {
        const pnl = t.pnl;
        if (pnl > 0) {
          pfGrossProfit += pnl;
          pfWinCount++;
        } else if (pnl < 0) {
          pfGrossLoss += Math.abs(pnl);
        }
        pfClosedCount++;
      }
    }
    let profitFactor;
    if (pfGrossLoss > 0) {
      profitFactor = pfGrossProfit / pfGrossLoss;
    } else if (pfGrossProfit > 0) {
      profitFactor = Infinity;
    } else {
      profitFactor = 0;
    }
    const winRate = pfClosedCount > 0 ? (pfWinCount / pfClosedCount) * 100 : 0;
    return {
      totalNetProfit,
      profitFactor,
      winRate,
      totalTrades: tradeCount
    };
  }

  // --- Обычный расчет ---
  let profitFactor;
  if (grossLoss > 0) {
    profitFactor = grossProfit / grossLoss;
  } else if (grossProfit > 0) {
    profitFactor = Infinity;
  } else {
    profitFactor = 0;
  }
  const winRate = closedTradeCount > 0 ? (winCount / closedTradeCount) * 100 : 0;

  return { totalNetProfit, profitFactor, winRate, totalTrades: tradeCount };
};


// ========================================================================
// 3. ИСПРАВЛЕННЫЙ КОМПОНЕНТ: TradeRow
//    Правильно определяет статус `isClosed` и отображает актуальные данные.
// ========================================================================
const TradeRow = ({ entries, exits, index, direction, trade }) => {
  const tempCandlesData = useRecoilValue(tempCandlesSelector);
  const lastCandle = tempCandlesData.length > 0 ? tempCandlesData[tempCandlesData.length - 1] : null;

  if (!entries?.length) {
    return null;
  }

  // --- Исправление: инвертируем знак PnL для шортов в режиме PortfolioManager ---
  // Определяем, был ли trade из PortfolioManager (по наличию поля pnl)
  const isPortfolioManagerTrade = typeof trade.pnl === 'number';

  let netPnl = calculateSingleTradePnl(entries, exits, direction, lastCandle, marketFee, cashbackRate);

  if (isPortfolioManagerTrade && direction === 'short') {
    netPnl = -netPnl;
  }

  const allActions = [...entries, ...(exits || [])];
  const lastActionInCycle = allActions[allActions.length - 1];
  const firstEntry = entries[0];
  const lastEntry = entries[entries.length - 1];
  // ИСПРАВЛЕНИЕ: Сделка считается закрытой, только если остаток позиции равен нулю.
  const isClosed = (lastActionInCycle.trade?.positionSize || 0) < 1e-6;
  const lastExit = isClosed && exits && exits.length > 0 ? exits[exits.length - 1] : null;

  const avgEntryPrice = lastEntry.trade?.avgEntryPrice;
  // Данные для колонок берем из последнего действия в цикле, чтобы они были актуальны.
  const entryPrice = avgEntryPrice || firstEntry.trade?.avgEntryPrice || firstEntry.value;
  const exitPrice = lastExit?.value || lastCandle?.close;
  // Отображаем либо финальный размер позиции (если сделка закрыта, будет 0), либо текущий оставшийся.
  const positionSize = lastEntry.trade?.positionSize
  const positionLeft = isClosed ? 0 : lastActionInCycle.trade?.positionSize;

  // --- Новое: вычисляем expectancy, если есть ---
  const expectancy = trade?.metadata?.expectancy || firstEntry.trade?.metadata?.expectancy || null;

  // colors
  const isTimeSwapped = exits.length ? lastEntry.time >= exits[0].time : false;
  const resultColor = isTimeSwapped
    ? 'warning.main'
    : netPnl > 0 ? 'success.main' : 'error.main';

  const priceDiffPercent = entryPrice > 0 && exitPrice ? ((exitPrice - entryPrice) / entryPrice) * 100 : 0;
  const percentColor = isTimeSwapped
    ? 'warning.main'
    : direction === 'long'
      ? (priceDiffPercent >= 0 ? 'success.main' : 'error.main')
      : (priceDiffPercent < 0 ? 'success.main' : 'error.main');

  const simpleExpColor = isTimeSwapped
    ? 'warning.main'
    : trade?.simpleExpectancy > 0
      ? 'success.main'
      : 'error.main';

  return (
    <TableRow
      sx={{
        '&:last-child td, &:last-child th': { border: 0 }
      }}
      onClick={() => {
        console.log('backtest row clicked', trade)
        const chart = getCandleChart();
        if (chart) {
          try {
            const timeScale = chart.timeScale();
            const targetTime = firstEntry.time;
            const coordinate = timeScale.timeToCoordinate(targetTime);

            const currentPosition = timeScale.scrollPosition();
            const currentVisibleLogicalRange = timeScale.getVisibleLogicalRange();
            const center = (currentVisibleLogicalRange.from + currentVisibleLogicalRange.to) / 2
            const targetPosition = currentPosition - center + timeScale.coordinateToLogical(coordinate);

            timeScale.scrollToPosition(targetPosition, false);
          } catch (e) {
            console.log('error', e)
          }
        }
      }}
    >
      <TableCell component="th" scope="row">{index + 1}</TableCell>
      {'showDirectionColumn' in trade && trade.showDirectionColumn ? (
        <TableCell>
          <span style={{
            fontWeight: 600,
            color: direction === 'long' ? '#388e3c' : '#d32f2f'
          }}>
            {direction === 'long' ? 'Long' : 'Short'}
          </span>
        </TableCell>
      ) : null}
      <TableCell>{formatTime(firstEntry.time)}</TableCell>
      <TableCell>{isClosed ? formatTime(lastExit.time) : 'N/A'}</TableCell>
      <TableCell align="right">{formatNumber(entryPrice, 4)}</TableCell>
      <TableCell align="right">{formatNumber(exitPrice, 4)}</TableCell>
      <TableCell align="right">{formatNumber(positionSize)} {positionLeft ? `(${formatNumber(positionLeft)})` : ''}</TableCell>
      <TableCell align="right" sx={{ color: percentColor }}>
        {exitPrice ? `${priceDiffPercent >= 0 ? '+' : ''}${formatNumber(priceDiffPercent, 2)}%` : 'N/A'}
      </TableCell>
      {/* Новая колонка: Expectancy */}
      <TableCell align="right" sx={{ color: expectancy > 0 ? 'success.main' : expectancy < 0 ? 'error.main' : undefined }}>
        {expectancy !== undefined ? formatNumber(expectancy * 100, 2) + '%' : ''}
      </TableCell>
      <TableCell align="right" sx={{ color: resultColor, fontWeight: 'bold' }}>
        {formatNumber(netPnl)}
      </TableCell>
      <TableCell align="left">
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {exits.map((exit, idx) => {
            const delta = direction === 'long'
              ? exit.value - entryPrice
              : entryPrice - exit.value
            const dp = delta / entryPrice
            const feePercent = marketFee * 2 * (1 - cashbackRate)

            const color = exit.reason === 'TP'
              ? dp >= feePercent ? 'success' : 'warning'
              : dp >= feePercent ? 'info' : 'error'

            return (
              <Chip
                key={idx}
                label={`${exit.reason} ${formatNumber(dp * 100, 3)}%`}
                size="small"
                color={color}
                variant="outlined"
              />
            )
          })}
          {!isClosed ?
            <Chip
              label={`Open ${formatNumber(priceDiffPercent, 3)}%`}
              size="small"
              color="warning"
              variant="outlined"
            /> : null}
        </Box>
      </TableCell>
    </TableRow>
  );
};


// ========================================================================
// 4. КОМПОНЕНТ: TradesTable (изменена только логика рендеринга строк)
// ========================================================================
const TradesTable = ({ title, actions, netProfitOverride }) => {
  const tempCandlesData = useRecoilValue(tempCandlesSelector);
  const lastCandle = tempCandlesData.length > 0 ? tempCandlesData[tempCandlesData.length - 1] : null;

  const direction = title.toLowerCase().includes('long') ? 'long' : 'short';
  const metrics = useMemo(() => calculateMetrics(actions, direction, lastCandle), [actions, direction, lastCandle]);

  // --- если передан netProfitOverride, используем его вместо стандартного ---
  const netProfit = typeof netProfitOverride === 'number' ? netProfitOverride : metrics.totalNetProfit;

  const renderTradeRows = () => {
    if (!actions || actions.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={9} align="center">No trades</TableCell>
        </TableRow>
      );
    }

    const rows = [];
    for (let i = 0; i < actions.length; i++) {
      const trade = actions[i]
      const entries = trade.entries || [];
      const exits = trade.exits || [];

      rows.push(
        <TradeRow
          key={i}
          index={i}
          trade={trade}
          entries={entries}
          exits={exits}
          direction={direction}
        />
      );
    }
    return rows;
  };


  return (
    <Box sx={{ mb: 4 }}>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={1} textAlign="center">
            <Grid item xs={1} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6">{title}</Typography>
            </Grid>
            <Grid item xs={5} md={2}>
              <Typography color="text.secondary">Net Profit</Typography>
              <Typography variant="h6" color={netProfit >= 0 ? 'success.main' : 'error.main'}>
                {formatNumber(netProfit)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography color="text.secondary">Profit Factor</Typography>
              <Typography variant="h6">{formatNumber(metrics.profitFactor)}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography color="text.secondary">Win Rate, %</Typography>
              <Typography variant="h6">{formatNumber(metrics.winRate)}%</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography color="text.secondary">Total Trades</Typography>
              <Typography variant="h6">{metrics.totalTrades}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table stickyHeader size="small" sx={{ minWidth: 900 }} aria-label={`${title} actions table`}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              {/* ...existing columns... */}
              <TableCell>Entry Time</TableCell>
              <TableCell>Exit Time</TableCell>
              <TableCell align="right">Avg. Entry</TableCell>
              <TableCell align="right">Avg. Exit</TableCell>
              <TableCell align="right">Position ($)</TableCell>
              <TableCell align="right">Change, %</TableCell>
              {/* Новая колонка */}
              <TableCell align="right">Expectancy</TableCell>
              <TableCell align="right">PnL ($)</TableCell>
              <TableCell align="center">Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTradeRows()}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};


// ========================================================================
// 5. Основной компонент BacktestResult (исправлен хук useMemo)
// ========================================================================

function SummaryBlock({ equity, balance, unrealizedPnl, showBalance }) {
  return (
    <Box sx={{
      mb: 3, p: 2,
      backgroundColor: 'action.hover',
      borderRadius: 1,
      display: 'flex',
      flexDirection: 'row', // <-- горизонтально
      alignItems: 'center',
      gap: 4,
    }}>
      <Box>
        <Typography color="text.secondary">
          Equity:
        </Typography>
        <Typography variant="h4">
          {formatNumber(equity, 5)}
        </Typography>
        {unrealizedPnl !== 0 && (
          <Typography variant="body2" color={unrealizedPnl >= 0 ? 'success.main' : 'error.main'}>
            (Unrealized PnL: {formatNumber(unrealizedPnl)})
          </Typography>
        )}
      </Box>
      {showBalance && (
        <Box>
          <Typography color="text.secondary">
            Balance:
          </Typography>
          <Typography variant="h4">
            {formatNumber(balance, 5)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// --- Блок результатов по направлению ---
function DirectionBlock({ title, actions, netProfitOverride }) {
  return (
    <TradesTable
      title={title}
      actions={actions}
      netProfitOverride={netProfitOverride}
    />
  );
}

// --- Модифицированная таблица для общего режима ---
const TradesTableUnified = ({ actions, netProfitOverride }) => {
  const tempCandlesData = useRecoilValue(tempCandlesSelector);
  const lastCandle = tempCandlesData.length > 0 ? tempCandlesData[tempCandlesData.length - 1] : null;

  // direction берём из trade.direction
  const metrics = useMemo(() => {
    // direction для unified не используется, но для совместимости передаем 'long'
    return calculateMetrics(actions, 'long', lastCandle);
  }, [actions, lastCandle]);

  const netProfit = typeof netProfitOverride === 'number' ? netProfitOverride : metrics.totalNetProfit;

  // --- сортировка по времени входа (первый entry) ---
  const sortedActions = useMemo(() => {
    return [...actions].sort((a, b) => {
      const aTime = a.entries?.[0]?.time ?? 0;
      const bTime = b.entries?.[0]?.time ?? 0;
      return aTime - bTime;
    });
  }, [actions]);

  const renderTradeRows = () => {
    if (!sortedActions || sortedActions.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={10} align="center">No trades</TableCell>
        </TableRow>
      );
    }

    return sortedActions.map((trade, i) => {
      const entries = trade.entries || [];
      const exits = trade.exits || [];
      const direction = trade.direction || 'long';
      return (
        <TradeRow
          key={i}
          index={i}
          trade={trade}
          entries={entries}
          exits={exits}
          direction={direction}
        />
      );
    });
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={1} textAlign="center">
            <Grid item xs={2} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6">All Trades</Typography>
            </Grid>
            <Grid item xs={5} md={2}>
              <Typography color="text.secondary">Net Profit</Typography>
              <Typography variant="h6" color={netProfit >= 0 ? 'success.main' : 'error.main'}>
                {formatNumber(netProfit)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography color="text.secondary">Profit Factor</Typography>
              <Typography variant="h6">{formatNumber(metrics.profitFactor)}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography color="text.secondary">Win Rate, %</Typography>
              <Typography variant="h6">{formatNumber(metrics.winRate)}%</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography color="text.secondary">Total Trades</Typography>
              <Typography variant="h6">{metrics.totalTrades}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <TableContainer component={Paper} variant="outlined">
        <Table stickyHeader size="small" sx={{ minWidth: 900 }} aria-label="all trades table">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Entry Time</TableCell>
              <TableCell>Exit Time</TableCell>
              <TableCell align="right">Avg. Entry</TableCell>
              <TableCell align="right">Avg. Exit</TableCell>
              <TableCell align="right">Position ($)</TableCell>
              <TableCell align="right">Change, %</TableCell>
              {/* Новая колонка */}
              <TableCell align="right">Expectancy</TableCell>
              <TableCell align="right">PnL ($)</TableCell>
              <TableCell align="center">Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTradeRows()}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const BacktestResult = () => {
  const [backtestResult] = useRecoilState(backtestResultState);
  const tempCandlesData = useRecoilValue(tempCandlesSelector);
  const [usePortfolioManager, setUsePortfolioManager] = useState(true);
  const [splitByDirection, setSplitByDirection] = useState(true);

  // --- Хелпер для Net Profit по закрытым сделкам PortfolioManager ---
  const getPortfolioManagerNetProfit = (trades) => {
    if (!trades) return 0;
    return trades.reduce((sum, t) => sum + (typeof t.pnl === 'number' ? t.pnl : 0), 0);
  };

  // --- Функции для отображения portfolioManager ---
  // Преобразуем portfolioManager.closedTrades в формат, совместимый с TradesTable и расчетами
  const getPortfolioManagerActions = (direction) => {
    if (!backtestResult?.portfolioManager?.closedTrades) return [];
    return backtestResult.portfolioManager.closedTrades
      .filter(trade => trade.direction === direction)
      .map(trade => {
        // --- КОПИРУЕМ МАССИВЫ, чтобы не мутировать исходные ---
        let positionSize = 0;
        let avgEntryPrice = 0;
        let entries = [];
        let exits = [];
        // Сортируем КОПИЮ entryOrders по времени исполнения
        const entryOrders = [...(trade.entryOrders || [])]
          .filter(e => e.status === 'filled')
          .sort((a, b) => (a.executionTime || 0) - (b.executionTime || 0));
        entryOrders.forEach(e => {
          // После исполнения этого ордера
          const prevPositionSize = positionSize;
          const prevAvgEntryPrice = avgEntryPrice;
          // Новая средняя цена входа
          const totalValue = prevAvgEntryPrice * prevPositionSize + e.price * e.size;
          positionSize += e.size;
          avgEntryPrice = positionSize > 0 ? totalValue / positionSize : 0;
          // deltaNet — сколько денег ушло на покупку (отрицательное)
          const deltaNet = -e.price * e.size;
          entries.push({
            value: e.price,
            time: e.executionTime,
            trade: {
              avgEntryPrice,
              positionSize,
              deltaNet,
            }
          });
        });
        // Теперь exits (exitOrders) — сортируем по времени
        const exitOrders = [...(trade.exitOrders || [])].sort((a, b) => (a.time || 0) - (b.time || 0));
        exitOrders.forEach(e => {
          // deltaNet — сколько денег вернулось (положительное)
          // positionSize уменьшается после выхода
          const deltaNet = e.price * e.size;
          positionSize -= e.size;
          exits.push({
            value: e.price,
            time: e.time,
            reason: e.reason,
            trade: {
              avgEntryPrice, // средняя цена входа на момент выхода
              positionSize: Math.max(positionSize, 0),
              deltaNet,
            }
          });
        });
        return {
          entries,
          exits,
          ...trade,
        };
      });
  };

  // Аналогично для открытых позиций (activePositions)
  const getPortfolioManagerOpenActions = (direction) => {
    if (!backtestResult?.portfolioManager?.activePositions) return [];
    return backtestResult.portfolioManager.activePositions
      .filter(trade => trade.direction === direction)
      .map(trade => {
        let positionSize = 0;
        let avgEntryPrice = 0;
        let entries = [];
        // Сортируем КОПИЮ entryOrders по времени исполнения
        const entryOrders = [...(trade.entryOrders || [])]
          .filter(e => e.status === 'filled')
          .sort((a, b) => (a.executionTime || 0) - (b.executionTime || 0));
        entryOrders.forEach(e => {
          const prevPositionSize = positionSize;
          const prevAvgEntryPrice = avgEntryPrice;
          const totalValue = prevAvgEntryPrice * prevPositionSize + e.price * e.size;
          positionSize += e.size;
          avgEntryPrice = positionSize > 0 ? totalValue / positionSize : 0;
          const deltaNet = -e.price * e.size;
          entries.push({
            value: e.price,
            time: e.executionTime,
            trade: {
              avgEntryPrice,
              positionSize,
              deltaNet,
            }
          });
        });
        // Нет выходов, позиция еще открыта
        return {
          entries,
          exits: [],
          ...trade,
        };
      });
  };

  // --- Выбор данных для таблиц ---
  let actionsLong = [];
  let actionsShort = [];
  let equity = 0;
  let unrealizedPnl = 0;
  let pmLongNetProfit = 0;
  let pmShortNetProfit = 0;
  let balance = 0;

  const lastCandle = tempCandlesData.length > 0 ? tempCandlesData[tempCandlesData.length - 1] : null;

  if (usePortfolioManager && backtestResult?.portfolioManager) {
    actionsLong = [
      ...getPortfolioManagerActions('long'),
      ...getPortfolioManagerOpenActions('long'),
    ];
    actionsShort = [
      ...getPortfolioManagerActions('short'),
      ...getPortfolioManagerOpenActions('short'),
    ];

    // Equity = balance + unrealized PnL по открытым позициям
    const pm = backtestResult.portfolioManager;
    let openPositionValue = 0;
    unrealizedPnl = 0;
    if (pm.activePositions && lastCandle) {
      pm.activePositions.forEach(pos => {
        if (pos.currentSize > 0) {
          const pnlMultiplier = pos.direction === 'long' ? 1 : -1;
          unrealizedPnl += (lastCandle.close - pos.averageEntryPrice) * pos.currentSize * pnlMultiplier;
          openPositionValue += pos.currentSize;
        }
      });
    }
    balance = pm.balance ?? 0;
    equity = balance + unrealizedPnl;

    // Net Profit по закрытым сделкам
    pmLongNetProfit = getPortfolioManagerNetProfit(
      pm.closedTrades?.filter(t => t.direction === 'long')
    );
    pmShortNetProfit = getPortfolioManagerNetProfit(
      pm.closedTrades?.filter(t => t.direction === 'short')
    );
  } else if (backtestResult?.state) {
    actionsLong = backtestResult.state.actionsLong || [];
    actionsShort = backtestResult.state.actionsShort || [];
    // Старый режим: equity = balance + openPositionValue + unrealizedPnl
    const { balance: bal = 0, actionsLong: al = [], actionsShort: as = [] } = backtestResult.state;
    balance = bal;
    let openPositionValueSum = 0;
    unrealizedPnl = 0;
    const tradesToProcess = [
      { actions: al, direction: 'long' },
      { actions: as, direction: 'short' }
    ];
    tradesToProcess.forEach(({ actions, direction }) => {
      for (let i = 0; i < actions.length; i++) {
        const trade = actions[i]
        const entries = trade.entries || [];
        const exits = trade.exits || [];
        if (!entries?.length) continue;
        const allActions = [...entries, ...(exits || [])];
        const lastAction = allActions[allActions.length - 1];
        const remainingPositionSize = lastAction.trade?.positionSize;
        if (remainingPositionSize > 1e-6) {
          const avgEntryPrice = lastAction.trade?.avgEntryPrice;
          const currentPrice = lastCandle?.close;
          if (avgEntryPrice && currentPrice) {
            let deltaPercent;
            if (direction === 'long') {
              deltaPercent = (currentPrice - avgEntryPrice) / avgEntryPrice;
            } else {
              deltaPercent = (avgEntryPrice - currentPrice) / avgEntryPrice;
            }
            const grossUnrealized = remainingPositionSize * deltaPercent;
            const closingFee = remainingPositionSize * marketFee;
            const feeCashback = closingFee * cashbackRate;
            const unrealizedPnlForThisTrade = grossUnrealized - closingFee + feeCashback;
            unrealizedPnl += unrealizedPnlForThisTrade;
          }
          openPositionValueSum += remainingPositionSize;
        }
      }
    });
    equity = balance + openPositionValueSum + unrealizedPnl;
  }

  if (!backtestResult || (!backtestResult.state && !backtestResult.portfolioManager)) {
    return (
      <Card><CardContent>
        <Typography variant="h6" color="text.secondary">No data to display</Typography>
      </CardContent></Card>
    );
  }

  // --- Собираем unified список сделок ---
  let unifiedActions = [];
  let unifiedNetProfit = 0;
  if (!splitByDirection) {
    // Объединяем все сделки и помечаем, что нужно показывать колонку Direction
    unifiedActions = [
      ...actionsLong.map(t => ({ ...t, showDirectionColumn: true, direction: t.direction || 'long' })),
      ...actionsShort.map(t => ({ ...t, showDirectionColumn: true, direction: t.direction || 'short' })),
    ];
    // Net Profit для unified: сумма по обоим направлениям
    unifiedNetProfit = usePortfolioManager
      ? (pmLongNetProfit + pmShortNetProfit)
      : undefined;
  }

  return (
    <Card sx={{ minWidth: 275, mt: 2 }}>
      <CardContent>
        {/* --- Переключатели --- */}
        <Box sx={{ mb: 2, display: 'flex', gap: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={usePortfolioManager}
                onChange={e => setUsePortfolioManager(e.target.checked)}
                color="primary"
              />
            }
            label="Portfolio Manager Mode"
          />
          <FormControlLabel
            control={
              <Switch
                checked={splitByDirection}
                onChange={e => setSplitByDirection(e.target.checked)}
                color="primary"
              />
            }
            label="Split by Direction"
          />
        </Box>
        <SummaryBlock
          equity={equity}
          balance={balance}
          unrealizedPnl={unrealizedPnl}
          showBalance={usePortfolioManager}
        />
        {splitByDirection ? (
          <>
            <DirectionBlock
              title="Long"
              actions={actionsLong}
              netProfitOverride={usePortfolioManager ? pmLongNetProfit : undefined}
            />
            <DirectionBlock
              title="Short"
              actions={actionsShort}
              netProfitOverride={usePortfolioManager ? pmShortNetProfit : undefined}
            />
          </>
        ) : (
          <TradesTableUnified
            actions={unifiedActions}
            netProfitOverride={usePortfolioManager ? unifiedNetProfit : undefined}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default BacktestResult;